import { useCallback, useEffect, useMemo, useState } from "react";
import { ANIMALS, GAME_CONFIG } from "../../constants";
import { useGame } from "../../context";
import { MathZooAPI } from "../../services/api";
import "./GameSelector.css";

// 獲取舒爾特方格的進度統計
const getSchulteGridProgress = () => {
  try {
    const history = JSON.parse(
      localStorage.getItem("schulteGridHistory") || "[]",
    );
    if (history.length === 0) {
      return { level: 1, correctCount: 0, totalPlayed: 0, totalScore: 0 };
    }

    // 計算答對題數（所有正確的格子點擊）
    const correctCount = history.reduce(
      (sum, record) => sum + (record.correctCount || 0),
      0,
    );

    // 計算總遊玩次數
    const totalPlayed = history.length;

    // 計算總分數
    const totalScore = history.reduce(
      (sum, record) => sum + (record.score || 0),
      0,
    );

    // 根據完成的最高難度計算等級
    const completedLevels = new Set();
    history.forEach((record) => {
      if (!record.timeout && record.accuracy >= 70) {
        // 從難度名稱推斷等級
        if (record.difficulty?.includes("初級") || record.gridSize === 3)
          completedLevels.add(1);
        if (record.difficulty?.includes("中級") || record.gridSize === 4)
          completedLevels.add(2);
        if (record.difficulty?.includes("高級") || record.gridSize === 5)
          completedLevels.add(3);
        if (record.difficulty?.includes("專家") || record.gridSize === 6)
          completedLevels.add(4);
      }
    });

    const level = completedLevels.size > 0 ? Math.max(...completedLevels) : 1;

    return { level, correctCount, totalPlayed, totalScore };
  } catch (e) {
    return { level: 1, correctCount: 0, totalPlayed: 0, totalScore: 0 };
  }
};

// 世界排行榜彈窗組件
// 頭像映射表 - iconCode 到 emoji 的對應
const AVATAR_ICONS = {
  1: "👨‍🎓",
  2: "👩‍🎓",
  3: "🧑‍🎓",
  4: "👨‍🏫",
  5: "👩‍🏫",
  6: "🧑‍🏫",
  7: "👨‍💻",
  8: "👩‍💻",
  9: "🧑‍💻",
  10: "👨‍🔬",
  11: "👩‍🔬",
  12: "🧑‍🔬",
  13: "👨",
  14: "👩",
  15: "🧑",
  16: "👦",
  17: "👧",
  18: "🧒",
  19: "😊",
  20: "😎",
  21: "🤓",
  22: "😇",
  23: "🥳",
  24: "🤔",
  25: "🐶",
  26: "🐱",
  27: "🐰",
  28: "🐼",
  29: "🦊",
  30: "🦁",
  31: "🐸",
  32: "🐵",
  33: "🐷",
  34: "🐻",
  35: "🐯",
  36: "🐨",
};

// 可選擇的頭像 iconCode 列表
const AVAILABLE_AVATARS = Object.keys(AVATAR_ICONS).map(Number);

// 獲取頭像 emoji 的輔助函數
const getAvatarEmoji = (iconCode) => {
  return AVATAR_ICONS[iconCode] || AVATAR_ICONS[1]; // 默認為第一個頭像
};

// 頭像選擇彈窗
const AvatarSelectorModal = ({
  onClose,
  currentAvatarCode,
  onSelectAvatar,
}) => {
  const [selectedAvatarCode, setSelectedAvatarCode] =
    useState(currentAvatarCode);

  const handleConfirm = () => {
    onSelectAvatar(selectedAvatarCode);
    onClose();
  };

  return (
    <div className="avatar-modal-overlay" onClick={onClose}>
      <div className="avatar-modal" onClick={(e) => e.stopPropagation()}>
        <div className="avatar-header">
          <h3>🎨 選擇頭像</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="current-selection">
          <span className="selection-label">當前選擇：</span>
          <span className="selected-avatar">
            {getAvatarEmoji(selectedAvatarCode)}
          </span>
        </div>

        <div className="avatar-grid">
          {AVAILABLE_AVATARS.map((iconCode) => (
            <button
              key={iconCode}
              className={`avatar-option ${
                selectedAvatarCode === iconCode ? "selected" : ""
              }`}
              onClick={() => setSelectedAvatarCode(iconCode)}
            >
              {getAvatarEmoji(iconCode)}
            </button>
          ))}
        </div>

        <div className="avatar-actions">
          <button className="cancel-btn" onClick={onClose}>
            取消
          </button>
          <button className="confirm-btn" onClick={handleConfirm}>
            確認
          </button>
        </div>
      </div>
    </div>
  );
};

const WorldLeaderboardModal = ({ onClose, currentUser, currentScore }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRank, setUserRank] = useState(null);

  // 獲取排行榜數據
  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const data = await MathZooAPI.getLeaderboard("score", 50);

        if (data.status === "success") {
          setLeaderboard(data.data || []);

          // 找出當前用戶的排名
          if (currentUser?.id) {
            const myRankIndex = data.data?.findIndex(
              (item) => item.userId === currentUser.id,
            );
            if (myRankIndex !== -1) {
              setUserRank(myRankIndex + 1);
            } else {
              // 用戶不在前50名，計算大概排名
              const lastScore =
                data.data?.[data.data.length - 1]?.totalScore || 0;
              if (currentScore > lastScore && data.data?.length >= 50) {
                setUserRank("50+");
              } else if (currentScore > 0) {
                // 根據分數估算排名
                const higherScores =
                  data.data?.filter((item) => item.totalScore > currentScore)
                    .length || 0;
                setUserRank(higherScores + 1);
              } else {
                setUserRank("-");
              }
            }
          }
        } else {
          setError("無法載入排行榜");
        }
      } catch (err) {
        console.error("Fetch leaderboard error:", err);
        setError("網路錯誤，請稍後再試");
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [currentUser, currentScore]);

  return (
    <div className="leaderboard-modal-overlay" onClick={onClose}>
      <div
        className="leaderboard-modal world-leaderboard"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="leaderboard-header">
          <h3>🌍 世界排行榜</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="user-rank-highlight">
          <div className="rank-info">
            <span className="rank-label">你的排名</span>
            <span className="rank-value">
              {userRank ? `第 ${userRank} 名` : "未上榜"}
            </span>
          </div>
          <div className="score-info">
            <span className="rank-score">
              {currentScore.toLocaleString()} 分
            </span>
            <span className="rank-user">{currentUser?.username || "訪客"}</span>
          </div>
        </div>

        <div className="leaderboard-list">
          {loading ? (
            <div className="loading">
              <span className="loading-spinner">🔄</span>
              載入中...
            </div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : leaderboard.length === 0 ? (
            <div className="empty">暫無排行數據，快去玩遊戲吧！</div>
          ) : (
            leaderboard.map((player, index) => (
              <div
                key={player.userId || index}
                className={`leaderboard-item ${
                  player.userId === currentUser?.id ? "current" : ""
                }`}
              >
                <span className="rank">
                  {index === 0
                    ? "🥇"
                    : index === 1
                    ? "🥈"
                    : index === 2
                    ? "🥉"
                    : `${index + 1}`}
                </span>
                <span className="player-avatar">
                  {player.avatar ? getAvatarEmoji(player.avatar) : "👤"}
                </span>
                <span className="player-name">
                  {player.username || "匿名玩家"}
                </span>
                <span className="player-score">
                  {(player.totalScore || 0).toLocaleString()} 分
                </span>
                <span className="player-stars">
                  ⭐ {player.totalStars || 0}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="leaderboard-footer">
          <span>💡 累積更多分數來提升排名！</span>
        </div>
      </div>
    </div>
  );
};

const GameSelector = ({ onSelectGame, onLogout }) => {
  const { state, actions } = useGame();
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);

  // 獲取用戶頭像 iconCode，默認為 1
  const getUserAvatarCode = useMemo(() => {
    const userAvatar = state.user?.avatar;
    if (userAvatar) {
      // 已經是 iconCode
      return userAvatar;
    }
    return 1; // 默認 iconCode
  }, [state.user?.avatar]);

  // 更新用戶頭像
  const handleAvatarChange = useCallback(
    async (newAvatarCode) => {
      const updatedUser = {
        ...state.user,
        avatar: newAvatarCode,
      };

      // 使用 context 的 updateUser action 更新狀態
      const result = await actions.updateUser(updatedUser);

      if (!result.success) {
        // 如果更新失敗，顯示錯誤訊息
        console.error("頭像更新失敗:", result.error);
        // 這裡可以添加錯誤提示給用戶
        actions.showFeedback(`更新頭像失敗: ${result.error}`, "error");
      }
    },
    [state.user, actions],
  );

  // 獲取舒爾特方格進度（使用 useMemo 避免重複計算）
  const schulteGridProgress = useMemo(() => getSchulteGridProgress(), []);

  // 計算總分數
  const totalGameScore = useMemo(() => {
    const otherGamesScore = Object.values(state.gameProgress).reduce(
      (sum, g) => sum + (g.totalScore || 0),
      0,
    );
    return otherGamesScore + schulteGridProgress.totalScore;
  }, [state.gameProgress, schulteGridProgress]);

  const games = [
    {
      type: "decomposition",
      name: GAME_CONFIG.GAME_NAMES.decomposition,
      description: GAME_CONFIG.GAME_DESCRIPTIONS.decomposition,
      icon: "🧮",
      animal: ANIMALS.rabbit,
      color: "#FFB6C1",
      bgGradient: "linear-gradient(135deg, #FFB6C1, #FFC0CB)",
    },
    {
      type: "placeValue",
      name: GAME_CONFIG.GAME_NAMES.placeValue,
      description: GAME_CONFIG.GAME_DESCRIPTIONS.placeValue,
      icon: "📊",
      animal: ANIMALS.bear,
      color: "#DEB887",
      bgGradient: "linear-gradient(135deg, #DEB887, #F5DEB3)",
    },
    {
      type: "multiples",
      name: GAME_CONFIG.GAME_NAMES.multiples,
      description: GAME_CONFIG.GAME_DESCRIPTIONS.multiples,
      icon: "🔢",
      animal: ANIMALS.frog,
      color: "#90EE90",
      bgGradient: "linear-gradient(135deg, #90EE90, #98FB98)",
    },
    {
      type: "sumToTen",
      name: "湊十對對碰",
      description: "找出相加等於10的數字配對卡片，全部消除即勝利！",
      icon: "🔟",
      animal: ANIMALS.cat || { emoji: "🐱" },
      color: "#FFD700",
      bgGradient: "linear-gradient(135deg, #FFD700, #FFFACD)",
    },
    {
      type: "schulteGrid",
      name: "舒爾特方格探險",
      description: "視覺掃描與注意力訓練",
      icon: "🧠",
      animal: ANIMALS.monkey || { emoji: "🐵" },
      color: "#87CEEB",
      bgGradient: "linear-gradient(135deg, #87CEEB, #B0E0E6)",
    },
    {
      type: "recipeZone",
      name: "美食食譜區",
      description: "探索美味料理的秘密，學習經典炸雞肉丸的製作！",
      icon: "🍗",
      animal: { emoji: "👨‍🍳" },
      color: "#FF8C42",
      bgGradient: "linear-gradient(135deg, #FF8C42, #FF6B35)",
    },
  ];

  return (
    <div className="game-selector">
      {/* 頭部 */}
      <header className="selector-header">
        <div className="header-left">
          <h1 className="app-title">
            <span className="title-icon">🦁</span>
            數學動物園
          </h1>
        </div>

        <div className="header-right">
          <div className="header-top-row">
            <div className="user-stats">
              <div className="stat-item">
                <span className="stat-icon">⭐</span>
                <span className="stat-value">{state.totalStars}</span>
              </div>
              <div className="stat-item">
                <span className="stat-icon">🏆</span>
                <span className="stat-value">{state.totalScore}</span>
              </div>
            </div>

            <button
              className="leaderboard-btn"
              onClick={() => setShowLeaderboard(true)}
              title="查看世界排行榜"
            >
              <span className="btn-icon">🌍</span>
              <span className="btn-text">世界排行</span>
            </button>
          </div>

          <div className="user-info">
            <button
              className="user-avatar clickable"
              onClick={() => setShowAvatarSelector(true)}
              title="點擊更換頭像"
            >
              {getAvatarEmoji(getUserAvatarCode)}
            </button>
            <span className="user-name">{state.user?.username}</span>
            <button className="logout-btn" onClick={onLogout}>
              登出
            </button>
          </div>
        </div>
      </header>

      {/* 歡迎訊息 */}
      <div className="welcome-section">
        <div className="welcome-mascot">
          <span className="mascot">🐼</span>
        </div>
        <div className="welcome-text">
          <h2>歡迎回來，{state.user?.username}！</h2>
          <p>選擇一個遊戲開始你的數學冒險吧！</p>
        </div>
      </div>
      {/* 背景裝飾 */}
      <div className="selector-background">
        {Object.values(ANIMALS).map((animal, index) => (
          <span
            key={animal.id}
            className="bg-animal"
            style={{
              left: `${(index * 12) % 90}%`,
              top: `${(index * 15) % 85}%`,
              animationDelay: `${index * 0.3}s`,
            }}
          >
            {animal.emoji}
          </span>
        ))}
      </div>

      {/* 遊戲卡片 */}
      <div className="games-grid">
        {games.map((game) => {
          // 舒爾特方格使用單獨的進度數據
          // RecipeZone 不需要進度數據
          const progress =
            game.type === "schulteGrid"
              ? schulteGridProgress
              : game.type === "recipeZone"
              ? null
              : state.gameProgress[game.type] || { level: 1, correctCount: 0 };

          return (
            <div
              key={game.type}
              className="game-card"
              style={{ background: game.bgGradient }}
              onClick={() => onSelectGame(game.type)}
            >
              <div className="card-header">
                <span className="card-icon">{game.icon}</span>
                <span className="card-animal">{game.animal.emoji}</span>
              </div>

              <div className="card-content">
                <h3 className="card-title">{game.name}</h3>
                <p className="card-description">{game.description}</p>
              </div>

              {progress && (
                <div className="card-stats">
                  <div className="card-stat">
                    <span className="stat-label">等級</span>
                    <span className="stat-value">Lv.{progress.level}</span>
                  </div>
                  <div className="card-stat">
                    <span className="stat-label">答對</span>
                    <span className="stat-value">
                      {progress.correctCount}題
                    </span>
                  </div>
                </div>
              )}

              {progress && (
                <div className="card-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${(progress.correctCount % 5) * 20}%` }}
                    />
                  </div>
                </div>
              )}

              <button className="play-button">
                {game.type === "recipeZone" ? "查看食譜 →" : "開始遊戲 →"}
              </button>
            </div>
          );
        })}
      </div>

      {/* 成就展示 */}
      <div className="achievements-section">
        <h3 className="section-title">
          <span className="title-icon">🌟</span>
          我的動物收藏
        </h3>
        <div className="animals-collection">
          {Object.values(ANIMALS).map((animal) => {
            const isUnlocked = state.unlockedAnimals.includes(animal.id);

            return (
              <div
                key={animal.id}
                className={`collection-item ${
                  isUnlocked ? "unlocked" : "locked"
                }`}
                title={isUnlocked ? animal.name : "???"}
              >
                <span className="collection-emoji">
                  {isUnlocked ? animal.emoji : "🔒"}
                </span>
                <span className="collection-name">
                  {isUnlocked ? animal.name : "???"}
                </span>
              </div>
            );
          })}
        </div>
        <p className="collection-hint">收集更多星星解鎖新動物！</p>
      </div>

      {/* 遊戲統計 */}
      <div className="stats-section">
        <h3 className="section-title">
          <span className="title-icon">📈</span>
          遊戲統計
        </h3>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-emoji">🎯</span>
            <span className="stat-number">
              {Object.values(state.gameProgress).reduce(
                (sum, g) => sum + g.correctCount,
                0,
              ) + schulteGridProgress.correctCount}
            </span>
            <span className="stat-label">總答對題數</span>
          </div>
          <div className="stat-card">
            <span className="stat-emoji">🎮</span>
            <span className="stat-number">
              {Object.values(state.gameProgress).reduce(
                (sum, g) => sum + g.totalPlayed,
                0,
              ) + schulteGridProgress.totalPlayed}
            </span>
            <span className="stat-label">總遊玩次數</span>
          </div>
          <div className="stat-card">
            <span className="stat-emoji">💰</span>
            <span className="stat-number">{totalGameScore}</span>
            <span className="stat-label">總得分</span>
          </div>
          <div
            className="stat-card clickable"
            onClick={() => setShowLeaderboard(true)}
          >
            <span className="stat-emoji">🌍</span>
            <span className="stat-number">世界排行</span>
            <span className="stat-label">點擊查看 →</span>
          </div>
        </div>
      </div>

      {/* 世界排行榜彈窗 */}
      {showLeaderboard && (
        <WorldLeaderboardModal
          onClose={() => setShowLeaderboard(false)}
          currentUser={state.user}
          currentScore={state.totalScore || totalGameScore}
        />
      )}

      {/* 頭像選擇彈窗 */}
      {showAvatarSelector && (
        <AvatarSelectorModal
          onClose={() => setShowAvatarSelector(false)}
          currentAvatarCode={getUserAvatarCode}
          onSelectAvatar={handleAvatarChange}
        />
      )}

      {/* 頁腳 */}
      <footer className="selector-footer">
        <p>🎓 數學動物園 - 讓學習變得有趣！</p>
      </footer>
    </div>
  );
};

export default GameSelector;
